import { useState, useRef, useEffect } from "react";
import type { Company, ThemeClasses } from "../types";
import { brandTextColor } from "../../../../../../config/theme";

interface CompanyActionsMenuProps {
  company: Company;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (company: Company) => void;
  onEdit: (company: Company) => void;
  onDelete?: (company: Company) => void;
  onActivate?: (company: Company) => void;
  onSoftDelete?: (company: Company) => void;
  onSources?: (company: Company) => void;
  onRetransmissions?: (company: Company) => void;
  fullWidth?: boolean;
}

export function CompanyActionsMenu({
  company,
  themeClasses,
  onDetails,
  onEdit,
  onDelete,
  onActivate,
  onSoftDelete,
  onSources,
  onRetransmissions,
  fullWidth = false,
}: CompanyActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const isSuspended = company.status_id === "Y2aD7bE1fF6jM3kO9hP5";

  // Solo las compañías de origen mapon/wialon pueden tener fuentes.
  // Las de origen v5 (o sin origen) solo manejan retransmisiones.
  const canHaveSources = (company.origin || []).some(
    (o) => o === "mapon" || o === "wialon",
  );

  const itemClass = `w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${themeClasses.text} hover:bg-primary-500/10 hover:text-primary-600 dark:hover:text-primary-400`;

  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={`${fullWidth ? "w-full justify-center" : ""} flex items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors text-white bg-gray-700 hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 ${
          open ? "ring-2 ring-primary-500/40" : ""
        }`}
      >
        <i className="ri-settings-3-line text-base"></i>
        <span>Acciones</span>
        <i
          className={`ri-arrow-down-s-line text-base transition-transform ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open && (
        <div
          className={`absolute right-0 z-30 mt-1 w-52 rounded-xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} shadow-xl py-1 overflow-hidden`}
        >
          <button
            type="button"
            className={itemClass}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDetails(company);
            }}
          >
            <i className="ri-eye-line" style={{ color: brandTextColor }}></i>
            Detalles
          </button>

          <button
            type="button"
            disabled={editLoading}
            className={`${itemClass} ${editLoading ? "opacity-70 cursor-wait" : ""}`}
            onClick={async (e) => {
              e.stopPropagation();
              setEditLoading(true);
              try {
                await Promise.resolve(onEdit(company));
              } finally {
                setEditLoading(false);
                setOpen(false);
              }
            }}
          >
            {editLoading ? (
              <i className="ri-loader-4-line animate-spin text-primary-500"></i>
            ) : (
              <i className={`ri-pencil-line ${themeClasses.textSecondary}`}></i>
            )}
            {editLoading ? "Cargando..." : "Editar"}
          </button>

          {onSources && canHaveSources && (
            <button
              type="button"
              className={itemClass}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onSources(company);
              }}
            >
              <i className="ri-database-2-line text-amber-500"></i>
              Fuentes
            </button>
          )}

          {onRetransmissions && (
            <button
              type="button"
              className={itemClass}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onRetransmissions(company);
              }}
            >
              <i className="ri-share-forward-line text-sky-500"></i>
              Retransmisiones
            </button>
          )}

          <div className={`my-1 border-t ${themeClasses.cardBorder}`}></div>

          {isSuspended
            ? onActivate && (
                <button
                  type="button"
                  className={itemClass}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onActivate(company);
                  }}
                >
                  <i className="ri-check-line text-green-500"></i>
                  Activar
                </button>
              )
            : onDelete && (
                <button
                  type="button"
                  className={itemClass}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onDelete(company);
                  }}
                >
                  <i className="ri-pause-circle-line text-amber-500"></i>
                  Suspender
                </button>
              )}

          {onSoftDelete && (
            <button
              type="button"
              className={itemClass}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onSoftDelete(company);
              }}
            >
              <i className="ri-delete-bin-line text-red-500"></i>
              Eliminar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
